import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { deleteChatConversation, fetchChatConversations } from 'services/travProAgentStream';

import { clearPersistedThread, getConversations, removeLocalConversation } from './smartAssistStorage';
import { RECENT_PORTAL_LIMIT } from './smartAssistUtils';
import type { ConversationEntry, Message } from './types';

export interface UseConversationListProps {
  appId?: number | string | null;
  baseUrl: string;
  getToken: () => Promise<string | null>;
  chatOpen: boolean;
  conversationIdRef: React.MutableRefObject<string | null>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  resolveEffectiveAppId: () => number | null;
  refreshServerConversationsRef: React.MutableRefObject<(() => Promise<void>) | null>;
}

export interface UseConversationListReturn {
  conversations: ConversationEntry[];
  setConversations: React.Dispatch<React.SetStateAction<ConversationEntry[]>>;
  sidebarRows: ConversationEntry[];
  recentForPortal: ConversationEntry[];
  deletingConvId: string | null;
  handleDeleteConversation: (conversationId: string) => Promise<void>;
}

export function useConversationList({
  appId,
  baseUrl,
  getToken,
  chatOpen,
  conversationIdRef,
  setMessages,
  resolveEffectiveAppId,
  refreshServerConversationsRef,
}: UseConversationListProps): UseConversationListReturn {
  const [conversations, setConversations] = useState<ConversationEntry[]>(() => {
    return getConversations();
  });
  const [apiConversations, setApiConversations] = useState<ConversationEntry[] | null>(null);
  const [deletingConvId, setDeletingConvId] = useState<string | null>(null);
  const prevChatOpenRef = useRef(false);

  // ─── Fetch API conversations ─────────────────────────────────────────────
  useEffect(() => {
    const request = { active: true };
    void (async () => {
      const n = Number(appId);
      const aid = n > 0 ? n : Number(import.meta.env.VITE_APP_ID);
      if (!aid || Number.isNaN(aid)) return;
      const token = await getToken();
      if (!token || !request.active) return;
      try {
        const rows = await fetchChatConversations({ baseUrl, token, appId: String(aid) });
        if (request.active) setApiConversations(rows);
      } catch {
        if (request.active) setApiConversations(null);
      }
    })();
    return () => {
      request.active = false;
    };
  }, [appId, baseUrl, getToken]);

  // ─── Refresh after chat closes ───────────────────────────────────────────
  const refreshServerConversations = useCallback(async (): Promise<void> => {
    const aid = resolveEffectiveAppId();
    if (!aid) return;
    const token = await getToken();
    if (!token) return;
    try {
      setApiConversations(await fetchChatConversations({ baseUrl, token, appId: String(aid) }));
    } catch {
      setApiConversations(null);
    }
  }, [resolveEffectiveAppId, baseUrl, getToken]);

  useEffect(() => {
    refreshServerConversationsRef.current = refreshServerConversations;
  }, [refreshServerConversations, refreshServerConversationsRef]);

  useEffect(() => {
    if (prevChatOpenRef.current && !chatOpen) void refreshServerConversations();
    prevChatOpenRef.current = chatOpen;
  }, [chatOpen, refreshServerConversations]);

  // ─── Delete ──────────────────────────────────────────────────────────────
  const handleDeleteConversation = useCallback(
    async (conversationId: string): Promise<void> => {
      const aid = resolveEffectiveAppId();
      if (!aid || !conversationId) return;
      const token = await getToken();
      if (!token) return;

      setDeletingConvId(String(conversationId));
      try {
        await deleteChatConversation({ baseUrl, token, conversationId });
      } catch {
        /* still remove locally */
      } finally {
        setDeletingConvId(null);
      }

      setApiConversations((prev) => {
        return Array.isArray(prev)
          ? prev.filter((c) => {
              return String(c.conversationId) !== String(conversationId);
            })
          : prev;
      });
      setConversations(removeLocalConversation(conversationId));

      if (String(conversationIdRef.current) === String(conversationId)) {
        conversationIdRef.current = null;
        clearPersistedThread(String(aid));
        if (chatOpen) setMessages([]);
      }
    },
    [resolveEffectiveAppId, baseUrl, getToken, chatOpen, conversationIdRef, setMessages],
  );

  // ─── Merged lists ────────────────────────────────────────────────────────
  const sidebarRows = useMemo<ConversationEntry[]>(() => {
    if (!Array.isArray(apiConversations)) return conversations;
    const apiIds = new Set(
      apiConversations.map((c) => {
        return String(c.conversationId);
      }),
    );
    return [
      ...apiConversations,
      ...conversations.filter((c) => {
        return c.conversationId && !apiIds.has(String(c.conversationId));
      }),
    ];
  }, [apiConversations, conversations]);

  const recentForPortal = useMemo<ConversationEntry[]>(() => {
    if (Array.isArray(apiConversations)) {
      const apiIds = new Set(
        apiConversations.map((c) => {
          return String(c.conversationId);
        }),
      );
      return [
        ...apiConversations,
        ...conversations.filter((c) => {
          return c.conversationId && !apiIds.has(String(c.conversationId));
        }),
      ].slice(0, RECENT_PORTAL_LIMIT);
    }
    return conversations.slice(0, RECENT_PORTAL_LIMIT);
  }, [apiConversations, conversations]);

  return {
    conversations,
    setConversations,
    sidebarRows,
    recentForPortal,
    deletingConvId,
    handleDeleteConversation,
  };
}
